import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import CloseIcon from '@mui/icons-material/Close';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import Dialog from 'src/components/ModalDialogMUI6/styled/Dialog';
import ButtonV1 from 'src/components/ModalDialogMUI6/styled/ButtonV1';
import PopConfirm from 'src/components/PopConfirm';
import Tooltip from 'src/components/Tooltip';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import ModalDialogVideo from 'src/components/ModalDialogVideo/index';
import helpers from 'src/helpers';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

function ModalDialogMUI6({
  onClose,
  onOk,
  okDisabled,
  okButtonProps,
  closeButtonProps,
  onClickThirdButton,
  thirdButtonProps,
  isVisible,
  content,
  loading,
  title,
  titleOptions,
  footer,
  closeText,
  okText,
  thirdButtonText,
  closeOnClickOnlyTheButton,
  isHeaderVisible,
  okStyleType,
  closeStyleType,
  thirdButtonStyleType,
  popConfirmProps,
  disabledOkTooltipTitle,
  buttonsListOrder,
  modalSize,
  showVideo,
  videoTooltip,
  videoURL,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [videoOpen, setVideoOpen] = useState(false);
  const handleVideoOpen = () => setVideoOpen(true);
  const handleVideoClose = () => setVideoOpen(false);

  const getTitle = useCallback((options) => isHeaderVisible && (
    <DialogTitle sx={{ pt: 1 }}>
      <Box
        component="div"
        sx={{
          padding: '0px 8px',
        }}
      >
        {(!options?.isCloseIconHid || (showVideo && videoURL)) && (
          <Box
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: '100%',
              marginRight: '-16px',
            }}
          >
            {showVideo && videoURL && (
              <Tooltip
                title={videoTooltip}
                placement="left-start"
              >
                <IconButton
                  color="secondary"
                  onClick={handleVideoOpen}
                  size="large"
                >
                  <SvgIcon fontSize="large">
                    <OndemandVideoIcon />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            )}
            {!options?.isCloseIconHid && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                size="medium"
              >
                <CloseIcon style={{ color: '#C7C7C7' }} />
              </IconButton>
            )}
          </Box>
        )}
        <Box
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Box
            component="div"
            sx={{
              width: '100%',
              textAlign: 'center',
              fontSize: '28px',
              fontWeight: 500,
              marginTop: '5px',
              color: '#263238',
            }}
          >
            {title}
          </Box>
        </Box>
      </Box>
    </DialogTitle>
  ), [isHeaderVisible, title, onClose, showVideo, videoTooltip, videoURL, handleVideoOpen]);

  const closeButton = useMemo(() => (
    <ButtonV1
      key="closeButton"
      onClick={onClose}
      color="primary"
      styleType={closeStyleType}
      {...closeButtonProps}
    >
      {closeText}
    </ButtonV1>
  ), [onClose, closeText, closeStyleType, closeButtonProps]);

  const okButton = useMemo(() => {
    if (!onOk) {
      return null;
    }

    const okBtn = (
      <ButtonV1
        key="okButton"
        onClick={onOk}
        color="primary"
        disabled={loading || okDisabled}
        styleType={okStyleType || 'ok'}
        {...okButtonProps}
      >
        {okText}
      </ButtonV1>
    );

    if (!helpers.isEmpty(popConfirmProps)) {
      return (
        <PopConfirm
          key="okButton"
          {...popConfirmProps}
        >
          {okBtn}
        </PopConfirm>
      );
    }

    // A disabled button fires no mouse events, so the tooltip needs a span
    // wrapper to have something to anchor to (same shape PopConfirm uses
    // for its own disabled-ok tooltip). Opt-in: without a title, the
    // button renders exactly as before.
    const isOkDisabled = loading || okDisabled || okButtonProps?.disabled;

    return isOkDisabled && !helpers.isEmpty(disabledOkTooltipTitle) ? (
      <TooltipMUI6 key="okButton" title={disabledOkTooltipTitle}>
        <span>{okBtn}</span>
      </TooltipMUI6>
    ) : okBtn;
  }, [
    onOk,
    okDisabled,
    okStyleType,
    okButtonProps,
    okText,
    popConfirmProps,
    disabledOkTooltipTitle,
    loading
  ]);

  const thirdButton = useMemo(() => (
    <ButtonV1
      key="thirdButton"
      onClick={onClickThirdButton}
      color="primary"
      disabled={loading}
      styleType={thirdButtonStyleType || 'white'}
      {...thirdButtonProps}
    >
      {thirdButtonText}
    </ButtonV1>
  ), [
    loading,
    onClickThirdButton,
    thirdButtonStyleType,
    thirdButtonProps,
    thirdButtonText,
  ]);

  const actionButtons = useMemo(() => (helpers.transform.toArray(buttonsListOrder).map((item) => {
    switch (item) {
      case 'close':
        return closeButton;
      case 'ok':
        return okButton;
      case 'third':
        return thirdButton;
      default:
        return null;
    }
  })), [buttonsListOrder, closeButton, okButton, thirdButton]);

  const dialogActions = useMemo(() => (
    <Box
      component="div"
      sx={{
        textAlign: 'center',
        padding: '0px 25px 16px 25px',
        width: '100%',
      }}
    >
      {actionButtons}
    </Box>
  ), [actionButtons]);

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        modalSize={modalSize}
        open={isVisible}
        aria-labelledby="responsive-dialog-title"
        scroll="body"
        {...!closeOnClickOnlyTheButton ? { onClose } : {}}
      >
        {getTitle(titleOptions)}
        <Box
          component={DialogContent}
          sx={{
            '& .center': {
              textAlign: 'center',
            },
          }}
        >
          <Box
            component="div"
            sx={{
              padding: '0px 8px',
            }}
          >
            {content}
          </Box>
        </Box>
        <DialogActions>
          {footer || dialogActions}
        </DialogActions>
      </Dialog>
      <ModalDialogVideo
        onClose={handleVideoClose}
        isVisible={videoOpen}
        videosrc={videoURL}
        title={videoTooltip}
        largeModal
      />
    </>
  );
}

ModalDialogMUI6.propTypes = {
  onClose: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  content: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  titleOptions: PropTypes.object,
  footer: PropTypes.node,
  onOk: PropTypes.func,
  okDisabled: PropTypes.bool,
  okButtonProps: PropTypes.object,
  closeButtonProps: PropTypes.object,
  onClickThirdButton: PropTypes.func,
  thirdButtonProps: PropTypes.object,
  closeText: PropTypes.string,
  okText: PropTypes.string,
  thirdButtonText: PropTypes.string,
  closeOnClickOnlyTheButton: PropTypes.bool,
  isHeaderVisible: PropTypes.bool,
  okStyleType: PropTypes.oneOf([
    'close',
    'ok',
    'white',
  ]),
  closeStyleType: PropTypes.oneOf([
    'close',
    'ok',
    'white',
  ]),
  thirdButtonStyleType: PropTypes.oneOf([
    'close',
    'ok',
    'white',
  ]),
  popConfirmProps: PropTypes.object,
  // Shown on hover when the Ok button is disabled, so the user learns WHY
  // it is disabled. Ignored when the button is enabled or the title is
  // empty (the default), so existing callers are unaffected.
  disabledOkTooltipTitle: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  buttonsListOrder: PropTypes.array,
  modalSize: PropTypes.oneOf([
    'xs',
    'sm',
    'xl',
  ]),
  showVideo: PropTypes.bool,
  videoTooltip: PropTypes.string,
  videoURL: PropTypes.string,
};

ModalDialogMUI6.defaultProps = {
  loading: false,
  title: null,
  titleOptions: {
    top: false,
    isCloseIconHid: false,
  },
  footer: null,
  onOk: null,
  okDisabled: false,
  okButtonProps: {},
  closeButtonProps: {},
  onClickThirdButton: null,
  thirdButtonProps: {},
  closeText: 'Close',
  okText: 'Ok',
  closeOnClickOnlyTheButton: false,
  isHeaderVisible: true,
  closeStyleType: 'close',
  popConfirmProps: null,
  disabledOkTooltipTitle: '',
  modalSize: null,
  buttonsListOrder: [
    'close',
    'ok',
  ],
  showVideo: false,
  videoTooltip: '',
  videoURL: '',
};

export default ModalDialogMUI6;
